import { createApi } from "@reduxjs/toolkit/query/react";
import fetchBaseQueryCSRF from "../helper";

// Types for Brand Pulse API responses
export type BrandSelectionResponse = {
    ok: boolean;
    data?: {
        suggested_brands?: Array<{
            name: string;
            domains: string[];
            competitor_paid_search_volume: string;
            region: string;
        }>;
        tracked_brands?: Array<{
            name: string;
            dbId: number;  // Database ID for deletion
            domains: string[];
            competitor_paid_search_volume: string;
            region: string;
        }>;
    };
    error?: string;
};

// Track brand response
export type TrackBrandResponse = {
    ok: boolean;
    data?: {
        id: number;
        brandName: string;
        brandDomain: string;
        country: string;
    };
    error?: string;
};

// Brand Pulse API
const brandPulse = createApi({
    baseQuery: fetchBaseQueryCSRF({ baseUrl: process.env.CROSS_API_URL }),
    reducerPath: "api.brandPulse",
    tagTypes: ["brandPulse"],
    endpoints: (builder) => ({

        // Get brand selection (suggested + tracked brands)
        getBrandSelection: builder.query<BrandSelectionResponse, {
            workspaceId: number;
            selectedCountry?: string;
        }>({
            query: ({ workspaceId, selectedCountry }) => {
                const params = new URLSearchParams();
                params.append("workspaceId", String(workspaceId));
                if (selectedCountry) {
                    params.append("selectedCountry", selectedCountry);
                }
                return {
                    url: `/brand-pulse/getBrandSelection?${params.toString()}`,
                    method: "GET",
                    headers: {
                        "Cache-Control": "no-cache",
                        Accept: "application/json",
                    },
                };
            },
            providesTags: ["brandPulse"],
        }),

        // Track a brand by its name and country
        trackBrand: builder.mutation<TrackBrandResponse, {
            workspaceId: number;
            brandName: string;
            country: string;
            brandDomain?: string;
        }>({
            query: ({ workspaceId, brandName, country, brandDomain }) => ({
                url: `/brand-pulse/trackBrand`,
                method: "POST",
                body: JSON.stringify({ workspaceId, brandName, country, brandDomain }),
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            }),
            invalidatesTags: ["brandPulse"],
        }),

        // Untrack a brand by its database ID
        untrackBrand: builder.mutation<{ ok: boolean; error?: string }, number>({
            query: (dbId) => ({
                url: `/brand-pulse/trackBrand/${dbId}`,
                method: "DELETE",
                headers: {
                    Accept: "application/json",
                },
            }),
            invalidatesTags: ["brandPulse"],
        }),
        getBrands: builder.query<
            any,
            { workspace_id: string; country: string; date: string, compareDate: string }
        >({
            query: ({ workspace_id, country, date, compareDate }) => ({
                url: `/brand-pulse/brands`,
                method: "POST",
                body: JSON.stringify({ workspace_id, country, date, compareDate }),
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            }),
            providesTags: ["brandPulse"],
        }),
        getBrand: builder.query<
            any,
            { workspace_id: any, domain: string, region: string }
        >({
            query: ({ workspace_id, domain, region }) => ({
                url: `/brand-pulse/brand`,
                method: "POST",
                body: JSON.stringify({ workspace_id, domain, region }),
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            }),
            providesTags: ["brandPulse"],
        }),


        getCountries: builder.query<
            { countries: Array<{ region_name: string; share_of_searches: number }> },
            { workspace_id: string; devices?: string[]; platforms?: string[] }
        >({
            query: ({ workspace_id, devices = [], platforms = [] }) => ({
                url: `/brand-pulse/countries`,
                method: "POST",
                body: JSON.stringify({
                    workspace: workspace_id,
                    devices,
                    platforms
                }),
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            }),
            providesTags: ["brandPulse"],
        }),

    }),
});

export const {
    useGetBrandSelectionQuery,
    useTrackBrandMutation,
    useUntrackBrandMutation,
    useGetBrandsQuery,
    useGetBrandQuery,
    useGetCountriesQuery
} = brandPulse;

export default brandPulse;


